import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchInfographicDetail,
  fetchInfographicsList,
  storeInfographicTime,
  toggleInfographicBookmark,
  toggleInfographicLike,
  toggleInfographicRead,
} from "../services/infographicsService";

function normalizeItem(item = {}, index = 0) {
  return {
    id: Number(item?.infographics_id || item?.infographic_id || item?.id || index + 1),
    serialNo: Number(item?.infographics_index || item?.infographic_index || index + 1),
    title: item?.name || "Infographic will be available soon.",
    description: item?.description || "",
    freePaid: String(item?.free_paid || "F").toUpperCase(),
    paymentStatus: Number(item?.payment_status || 0),
    isRead: Number(item?.is_infographics_read || item?.view_status || 0) === 1,
    isBookmarked: Number(item?.bookmark_status || 0) === 1,
    isLiked: Number(item?.like_status || 0) === 1,
    likeCount: Number(item?.total_likes || 0),
    viewCount: Number(item?.total_views || 0),
    notesCount: Number(item?.total_infographics || 0),
    reportCount: item?.total_reports || "0/0",
    viewLabel: item?.infographics_view_at || "",
    subscriptionMessage: item?.subscription_message || "",
    raw: item,
  };
}

export function useInfographicsList(params = {}, options = {}) {
  return useQuery({
    queryKey: ["infographicsList", params],
    queryFn: async () => {
      const { payload } = await fetchInfographicsList(params);
      const pagination = payload?.pagination || {};
      return {
        stateName: payload?.state_name || "",
        subjectName: payload?.subject_name || "",
        topicName: payload?.topic_name || "",
        categoryName: payload?.category_name || "Infographics",
        totalResults: Number(payload?.total_infographics_count || pagination?.totalRecords || 0),
        items: (Array.isArray(payload?.infographics_list) ? payload.infographics_list : []).map(normalizeItem),
        pagination: {
          currentPage: Number(pagination?.currentPage || 1),
          totalPages: Number(pagination?.totalPages || 1),
          totalRecords: Number(pagination?.totalRecords || 0),
        },
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

export function useInfographicDetail(params = {}, options = {}) {
  return useQuery({
    queryKey: ["infographicDetail", params],
    queryFn: async () => {
      const { payload } = await fetchInfographicDetail(params);
      return {
        ...payload,
        infographic: payload?.infographics_data || null,
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

function updateListItem(data, infographicId, update) {
  if (!data?.items) return data;
  return { ...data, items: data.items.map((item) => Number(item.id) === Number(infographicId) ? update(item) : item) };
}

export function useInfographicActions(listParams = {}) {
  const queryClient = useQueryClient();
  const listKey = ["infographicsList", listParams];
  const updateDetail = (infographicId, update) => {
    queryClient.setQueriesData({ queryKey: ["infographicDetail"] }, (current) => {
      if (Number(current?.infographic?.infographic_id) !== Number(infographicId)) return current;
      return { ...current, infographic: { ...current.infographic, ...update(current.infographic) } };
    });
  };

  const bookmark = useMutation({
    mutationFn: toggleInfographicBookmark,
    onMutate: async ({ infographics_id }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData(listKey);
      queryClient.setQueryData(listKey, (data) => updateListItem(data, infographics_id, (item) => ({ ...item, isBookmarked: !item.isBookmarked })));
      updateDetail(infographics_id, (item) => ({ ...item, bookmark_status: Number(item.bookmark_status || 0) === 1 ? 0 : 1 }));
      return { previous };
    },
    onError: (_error, _variables, context) => context?.previous && queryClient.setQueryData(listKey, context.previous),
    onSuccess: ({ payload }, { infographics_id }) => {
      const bookmarked = Number(payload?.bookmark_status || 0) === 1;
      queryClient.setQueryData(listKey, (data) => updateListItem(data, infographics_id, (item) => ({ ...item, isBookmarked: bookmarked })));
      updateDetail(infographics_id, (item) => ({ ...item, bookmark_status: bookmarked ? 1 : 0 }));
    },
  });
  const like = useMutation({
    mutationFn: toggleInfographicLike,
    onMutate: async ({ infographics_id }) => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData(listKey);
      queryClient.setQueryData(listKey, (data) => updateListItem(data, infographics_id, (item) => {
        const isLiked = !item.isLiked;
        return { ...item, isLiked, likeCount: Math.max(0, item.likeCount + (isLiked ? 1 : -1)) };
      }));
      return { previous };
    },
    onError: (_error, _variables, context) => context?.previous && queryClient.setQueryData(listKey, context.previous),
    onSuccess: ({ payload }, { infographics_id }) => {
      const isLiked = Number(payload?.like_status || 0) === 1;
      const likeCount = Number(payload?.total_likes || 0);
      queryClient.setQueryData(listKey, (data) => updateListItem(data, infographics_id, (item) => ({ ...item, isLiked, likeCount })));
      updateDetail(infographics_id, (item) => ({ ...item, like_status: isLiked ? 1 : 0, total_likes: likeCount }));
    },
  });
  const markRead = useMutation({
    mutationFn: toggleInfographicRead,
    onSuccess: ({ payload }, { infographics_id }) => {
      const isRead = Number(payload?.view_status || 0) === 1;
      queryClient.setQueryData(listKey, (data) => updateListItem(data, infographics_id, (item) => ({ ...item, isRead })));
      updateDetail(infographics_id, (item) => ({ ...item, view_status: isRead ? 1 : 0 }));
    },
  });
  return { bookmark, like, markRead, storeTime: useMutation({ mutationFn: storeInfographicTime }) };
}
